/**
 * PATH: packages/email-hub/src/feature/components/email-preview-pane/usePreviewBody.ts
 * FILE_ID: EMAILHUB-EMAILPREVIEWPANE-BODY-HOOK-001
 * PURPOSE: Own body refs, find state, image interactions, and refresh events.
 */
import { useCallback, useEffect, useRef } from 'react'
import { useInPageFind } from '@quotech/ui'
import { useEmailBodyImageInteractions } from '../../hooks/useEmailBodyImageInteractions'
import { useInlineImageRecovery } from '../../hooks/useInlineImageRecovery'
import type { PreviewBodyRefs } from './types'

export function assignMaybeRef<T>(
  ref:
    | { current: T | null }
    | ((value: T | null) => void)
    | null
    | undefined,
  value: T | null,
): void {
  if (typeof ref === 'function') {
    ref(value)
    return
  }
  if (ref && typeof ref === 'object') {
    ref.current = value
  }
}

export function usePreviewBody(
  emailId: string,
  onRefetch?: () => void,
): PreviewBodyRefs {
  const imageInteractions = useEmailBodyImageInteractions(emailId)
  const { bodyRef } = imageInteractions
  const { bodyRef: inlineImageBodyRef } = useInlineImageRecovery({
    emailId: emailId ? String(emailId) : null,
    refetch: () => onRefetch?.(),
  })
  const findBodyRef = useRef<HTMLDivElement | null>(null)
  const combinedBodyRef = useCallback(
    (element: HTMLDivElement | null) => {
      assignMaybeRef(
        bodyRef as { current: HTMLDivElement | null } | undefined,
        element,
      )
      assignMaybeRef(
        inlineImageBodyRef as { current: HTMLDivElement | null } | undefined,
        element,
      )
      findBodyRef.current = element
    },
    [bodyRef, inlineImageBodyRef],
  )
  const find = useInPageFind({
    containerRef: findBodyRef,
    id: 'email-preview',
    label: 'email',
    enabled: Boolean(emailId),
  })

  useEffect(() => {
    const onChanged = (event: Event) => {
      const detail = (event as CustomEvent<{ emailId?: string }>).detail
      if (detail?.emailId && String(detail.emailId) === String(emailId)) {
        onRefetch?.()
      }
    }
    window.addEventListener('quotech:email-attachments-changed', onChanged)
    return () =>
      window.removeEventListener('quotech:email-attachments-changed', onChanged)
  }, [emailId, onRefetch])

  return {
    combinedBodyRef,
    findBodyRef,
    find,
    imageInteractions,
  }
}
